import { Box, Grid, MenuItem, Typography } from '@material-ui/core'
import React, { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ControlledTextField } from '../../../form/ControlledTextField'
import { theme } from '../../../../theme'
import { validators } from '../../../../validation/validators'
import {
  areaUnit,
  powerPer1000Unit,
  powerUnit,
  temperatureKelvinUnit,
  temperatureUnit,
} from '../../../../util/formatters'
import {
  EnergySystemType,
  ScheduleFragment,
  UpdateEnergySystemInput,
  UpdateScheduleInput,
} from '../../../../generated/graphql'
import {
  InputTabFormValues,
  saveInputData,
  updateModel,
  useInputTabStyles,
} from './input-util'

type Props = {
  id: string
  schedules: ScheduleFragment[]
  defaultValues: InputTabFormValues | undefined
  onUpdate: (
    esInput: UpdateEnergySystemInput,
    scheduleId?: string,
    scheduleInput?: UpdateScheduleInput,
  ) => void
  onDbUpdate: (
    esId: string,
    esInput: UpdateEnergySystemInput,
    scheduleIds?: string[],
    scheduleInputs?: UpdateScheduleInput[],
  ) => Promise<void>
}

export function CalibrationInputCoolingTab({
  id,
  schedules,
  defaultValues,
  onUpdate,
  onDbUpdate,
}: Props): React.ReactElement {
  const classes = useInputTabStyles()
  const { t } = useTranslation()

  const { control, errors, reset, formState } = useForm<InputTabFormValues>({
    defaultValues,
    mode: 'onBlur',
  })

  const dbUpdateNeededRef = useRef(false)

  const updateModelData = async (values: InputTabFormValues) => {
    await updateModel({
      values,
      defaultValues: defaultValues ?? ({} as InputTabFormValues),
      esType: EnergySystemType.Cooling,
      control,
      formState,
      updateStateRef: dbUpdateNeededRef,
      onReset: reset,
      onUpdate,
    })
  }

  const saveChanges = async () => {
    await saveInputData({ energySystemId: id, control, onDbUpdate })
  }

  useEffect(() => {
    void updateModelData(defaultValues ?? ({} as InputTabFormValues))

    return () => {
      if (dbUpdateNeededRef.current && defaultValues) {
        void saveChanges()
        dbUpdateNeededRef.current = false
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <form style={{ marginTop: theme.spacing(2) }}>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <Box className={classes.inputTab}>
            <Typography>
              {t('energySystem.parameters.cooling.tabs.systemCooling')}
            </Typography>
            <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.cooling.systemCoolingPower"
                  error={errors.values?.parameters?.cooling?.systemCoolingPower}
                  label={t(
                    'energySystem.parameters.cooling.systemCoolingPower',
                  )}
                  rules={{ ...validators.required, ...validators.decimal }}
                  size="small"
                  InputProps={{
                    endAdornment: <Typography>{powerUnit}</Typography>,
                  }}
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.scheduleId"
                  error={errors.values?.scheduleId}
                  label={t('energySystem.parameters.cooling.usageProfile')}
                  rules={validators.required}
                  size="small"
                  disabled
                  select
                >
                  {Object.values(schedules).map((schedule) => (
                    <MenuItem key={schedule.id} value={schedule.id}>
                      {schedule.name}
                    </MenuItem>
                  ))}
                </ControlledTextField>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box className={classes.inputTab}>
            <Typography>
              {t('energySystem.parameters.cooling.tabs.spaceCooling')}
            </Typography>
            <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.cooling.scStartTemp"
                  error={errors.values?.parameters?.cooling?.scStartTemp}
                  label={t('energySystem.parameters.cooling.scStartTemp')}
                  rules={{ ...validators.required, ...validators.decimal }}
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.cooling.scIncreaseSlope"
                  error={errors.values?.parameters?.cooling?.scIncreaseSlope}
                  label={t('energySystem.parameters.cooling.scIncreaseSlope')}
                  rules={{ ...validators.required, ...validators.decimal }}
                  InputProps={{
                    endAdornment: (
                      <Typography>
                        {powerPer1000Unit}/{areaUnit}/{temperatureKelvinUnit}
                      </Typography>
                    ),
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.cooling.scIncreaseExtraCoef"
                  error={
                    errors.values?.parameters?.cooling?.scIncreaseExtraCoef
                  }
                  label={t(
                    'energySystem.parameters.cooling.scIncreaseExtraCoef',
                  )}
                  rules={{ ...validators.required, ...validators.decimal }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.cooling.scExtraCoefStartTemp"
                  error={
                    errors.values?.parameters?.cooling?.scExtraCoefStartTemp
                  }
                  label={t(
                    'energySystem.parameters.cooling.scExtraCoefStartTemp',
                  )}
                  rules={{ ...validators.required, ...validators.decimal }}
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.cooling.scDistributionEfficiency"
                  error={
                    errors.values?.parameters?.cooling?.scDistributionEfficiency
                  }
                  label={t(
                    'energySystem.parameters.cooling.scDistributionEfficiency',
                  )}
                  rules={{ ...validators.required, ...validators.decimal }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.cooling.scSpacesPortion"
                  error={errors.values?.parameters?.cooling?.scSpacesPortion}
                  label={t('energySystem.parameters.cooling.scSpacesPortion')}
                  rules={{ ...validators.required, ...validators.decimal }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box className={classes.inputTab}>
            <Typography>
              {t('energySystem.parameters.cooling.tabs.ahuCooling')}
            </Typography>
            <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.cooling.acDemandDisplacementConstant"
                  error={
                    errors.values?.parameters?.cooling
                      ?.acDemandDisplacementConstant
                  }
                  label={t(
                    'energySystem.parameters.cooling.acDemandDisplacementConstant',
                  )}
                  rules={{ ...validators.required, ...validators.decimal }}
                  InputProps={{
                    endAdornment: (
                      <Typography>{temperatureKelvinUnit}</Typography>
                    ),
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.cooling.acCorrectionCoef"
                  error={errors.values?.parameters?.cooling?.acCorrectionCoef}
                  label={t('energySystem.parameters.cooling.acCorrectionCoef')}
                  rules={{ ...validators.required, ...validators.decimal }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.cooling.acIncreaseExtraCoef"
                  error={
                    errors.values?.parameters?.cooling?.acIncreaseExtraCoef
                  }
                  label={t(
                    'energySystem.parameters.cooling.acIncreaseExtraCoef',
                  )}
                  rules={{ ...validators.required, ...validators.decimal }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.cooling.acExtraCoefStartTemp"
                  error={
                    errors.values?.parameters?.cooling?.acExtraCoefStartTemp
                  }
                  label={t(
                    'energySystem.parameters.cooling.acExtraCoefStartTemp',
                  )}
                  rules={{ ...validators.required, ...validators.decimal }}
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.cooling.acDistributionEfficiency"
                  error={
                    errors.values?.parameters?.cooling?.acDistributionEfficiency
                  }
                  label={t(
                    'energySystem.parameters.cooling.acDistributionEfficiency',
                  )}
                  rules={{ ...validators.required, ...validators.decimal }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </form>
  )
}
